/**
 * Field — labelled text input primitive (design-revamp / handoff-2026-06).
 *
 * Production translation of the prototype `Field`
 * (`docs/GardEn/handoff-2026-06/garden/ui.jsx`). The prototype tracks focus
 * with React state to toggle the focus ring; here the ring is expressed with
 * `focus-within:` utilities so it is CSS-driven (no state, no re-render).
 *
 * Anatomy: optional `label` → bordered input shell (leading icon, optional
 * `prefix`, the `<input>`, optional `trailing` node) → optional `hint`/`error`
 * line. `error` flips the border + helper text to `--g-error` and sets
 * `aria-invalid`. The input uses `tabular-nums` so numeric entry (price,
 * area, phone) aligns. Ref forwards to the underlying `<input>` so RHF /
 * focus management works.
 */
import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";

import { Icon, type IconName } from "./Icon";

export interface FieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "prefix"> {
  /** Field label rendered above the input. */
  label?: string;
  /** Helper text below the input (suppressed when `error` is present). */
  hint?: string;
  /** Error text below the input — also flips the border + label tone. */
  error?: string;
  /** Leading icon name inside the input shell. */
  leading?: IconName;
  /** Trailing node inside the input shell (e.g. a unit, a clear button). */
  trailing?: ReactNode;
  /** Static prefix shown before the input value (e.g. a "+996" dial code). */
  prefix?: string;
  /** Classes for the outer wrapper (`<div>`), not the input. */
  className?: string;
}

/**
 * Renders a labelled input with optional leading icon, prefix, trailing
 * node, and hint/error helper line. `data-testid="g-field"` on the wrapper;
 * the input itself is `g-field-input`.
 */
function FieldImpl(
  {
    label,
    hint,
    error,
    leading,
    trailing,
    prefix,
    className,
    id,
    ...rest
  }: FieldProps,
  ref: Ref<HTMLInputElement>,
): ReactElement {
  const reactId = useId();
  const inputId = id ?? `g-field-${reactId.replace(/:/g, "")}`;
  const helperId = `${inputId}-helper`;
  const hasError = Boolean(error);
  const helper = error ?? hint;

  const shellClasses = [
    "flex items-center gap-2",
    "h-[52px] px-3.5",
    "bg-[var(--g-surface)]",
    "rounded-[var(--g-radius)]",
    "border",
    "transition-[border-color,box-shadow] duration-150",
    hasError
      ? "border-[var(--g-error)] focus-within:ring-[3px] focus-within:ring-[var(--g-error)]/20"
      : "border-border focus-within:border-primary focus-within:ring-[3px] focus-within:ring-[var(--g-primary-soft)]",
  ].join(" ");

  return (
    <div
      data-testid="g-field"
      className={["flex flex-col", className ?? ""].filter(Boolean).join(" ")}
    >
      {label && (
        <label
          htmlFor={inputId}
          className="mb-[7px] block text-[13px] font-semibold text-foreground"
        >
          {label}
        </label>
      )}
      <div className={shellClasses}>
        {leading && (
          <Icon
            name={leading}
            size={18}
            className="shrink-0 text-[var(--g-ink-3)]"
          />
        )}
        {prefix && (
          <span className="text-[15px] font-semibold tabular-nums text-[var(--g-ink-2)]">
            {prefix}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          data-testid="g-field-input"
          aria-invalid={hasError || undefined}
          aria-describedby={helper ? helperId : undefined}
          className="min-w-0 flex-1 border-none bg-transparent text-[15px] tabular-nums text-foreground outline-none placeholder:text-[var(--g-ink-3)]"
          {...rest}
        />
        {trailing}
      </div>
      {helper && (
        <div
          id={helperId}
          data-testid="g-field-helper"
          className={[
            "mt-1.5 text-xs",
            hasError ? "text-[var(--g-error)]" : "text-[var(--g-ink-3)]",
          ].join(" ")}
        >
          {helper}
        </div>
      )}
    </div>
  );
}

/**
 * `forwardRef` wrapper so callers can focus/measure the underlying input
 * (React Hook Form `register`, programmatic focus, etc.).
 */
export const Field = forwardRef<HTMLInputElement, FieldProps>(FieldImpl);
Field.displayName = "Field";
